<script>
    import { onMount } from "svelte";

    import { Loader } from "../../../../Components/Common/index.svelte";
    import { isObjectEmpty, showError } from "../../../../Utils/utilFunctions.svelte";

    import {
        workersCompensation,
        trafficAccidentAuthorities,
    } from "../../../../stores";
    import CustomTooltip from "../../../../Components/Common/CustomTooltip.svelte";
    import NotificationModal from "../../../../Components/Common/NotificationModal.svelte";
    import MatchedPatientEncountersListDetails from "./MatchedPatientEncountersListDetails.svelte";

    export let matchPatientStatus = 0,
        matchResult = {},
        isPatientMatched = false,
        patientDetails = {},
        patientContactDetails = {};

    let isFromCreate = true,
        isFromQuote = false;

    export let insuranceDetails = {},
        workerCompensation,
        workerCompensationObj = {},
        trafficAuthority,
        trafficAuthorityObj = {},
        publicInPrivateObj = {},
        enableChangeEncounterDoctor = false;

    export let insuranceDetailsRef;

    let defaultPatientDetails = {},
        viewMatchesModalOpen = false;

    $: displayMatchPatientStatus = _getMatchPatientStatus(matchPatientStatus);

    $: isSamePatient = _checkSamePatient(matchResult);

    onMount(() => {
        defaultPatientDetails = { ...patientDetails };
    });

    const applyPatientDetails = async (matchedPatient) => {
        if(matchedPatient){
            patientDetails = {
                ...matchedPatient,
                birthDate: matchedPatient.birthDate.split("T")[0],
            };

            const { patientContacts } = matchedPatient;

            if (patientContacts && patientContacts.length) {
                if (patientContacts.some((contact) => contact.useAsContact))
                    patientContactDetails = patientContacts.find(
                        (contact) => contact.useAsContact,
                    );
                else patientContactDetails = patientContacts[0];
            } else {
                patientContactDetails = {}
            }

            await _syncInsuranceDetails(matchedPatient);

            matchResult = {};

            isPatientMatched = true;
        } else {
            showError("Couldn't match patient details.");
        }
    };

    const viewMatches = async () => {
        viewMatchesModalOpen = true;    
    };

    // HELPER FUNCTIONS
    const _getMatchPatientStatus = (status) => {
        const res = {};

        switch (status) {
            case 0:
            default:
                res.displayStatus = "";
                res.textStyle = "";
                isPatientMatched = false;
                break;
            case 1:
                res.displayStatus = "Matching patient";
                res.textStyle = "";
                isPatientMatched = false;
                break;
            case 2:
                res.displayStatus = "Matched patient details applied";
                res.textStyle = "text-[#006400] text-lg";
                res.tooltipMatchApplied = "Matched patient details were successfully applied. If you change patient details now matching will run again."
                res.tooltipSingleMatch = "One patient match was found, click to apply all the details.";
                res.tooltipMultipleMatches = "Multiple previous patient encounters were found, click to see potential patient matches";
                isPatientMatched = true;
                break;
            case 3:
                res.displayStatus = "No patient match found";
                res.textStyle = "text-error text-lg";
                res.tooltip = "Patient matching occurs when at least 3 of these criteria match: 1. First, middle and last name 2. Date of birth 3. Gender 4. Medicare details 5. Private health fund and membership details 6. DVA veteran number"
                isPatientMatched = false;
                break;
        }

        return res;
    };

    const _checkSamePatient = (matchResult) => {
        if (isObjectEmpty(matchResult) || matchResult.results.length == 0) return true;

        for(let i = 0; i < matchResult.results.length; i++) {
            const { id } =
                matchResult.results[i].patient;
            if(isObjectEmpty(patientDetails)) continue;
            const {
                id: did,
            } = patientDetails;

            if (
                did == id
            ){
                console.log("same patient that was matched found");
                return true;
            }
        }
    };

    const _syncInsuranceDetails = async (matchedPatient) => {
        const {
            insuranceType,
            insuranceId,
            insurance,
            membershipNo,
            membershipRefNo,
        } = matchedPatient;

        switch (insuranceType) {
            case 0:
                // Private
                insuranceDetails.membershipNo = membershipNo;
                insuranceDetails.membershipRefNo = membershipRefNo;
                patientDetails.insuranceId = insuranceId;
                await insuranceDetailsRef.loadInsuranceFund();
                await insuranceDetailsRef._syncInsuranceFund();
                break;
            case 10:
                // Overseas
                insuranceDetails.overseasCover = insurance;
                insuranceDetails.membershipNo = membershipNo;
                insuranceDetails.membershipRefNo = membershipRefNo;
                patientDetails.insuranceId = insuranceId;
                await insuranceDetailsRef.loadInsuranceFund();
                await insuranceDetailsRef._syncInsuranceFund();
                break;
            case 16:
                // Workers Compensation
                if (isFromQuote) break;

                const wcTempArr = insurance.replaceAll(" ", "").split("|");

                if (wcTempArr.length == 3) {
                    insuranceDetails.employer = wcTempArr[0];
                    insuranceDetails.employerEmail = wcTempArr[1];
                    workerCompensationObj.claimNumber = wcTempArr[2];
                } else if (wcTempArr.length == 4) {
                    insuranceDetails.employer = wcTempArr[2];
                    insuranceDetails.employerEmail = wcTempArr[3];
                    workerCompensationObj.insurerEmail = "";
                }

                await insuranceDetailsRef.loadWorkerCompensation(false);

                if ($workersCompensation.length)
                    workerCompensation = $workersCompensation.find(
                        (compensation) => compensation.id == insuranceId,
                    );

                if (workerCompensation?.id)
                    workerCompensationObj.insurerEmail =
                        workerCompensation.email;
                break;
            case 17:
                // Traffic Accident
                if (isFromQuote) break;

                trafficAuthorityObj.claimNumber = insurance;

                await insuranceDetailsRef.loadTrafficAuthorities(false);

                if ($trafficAccidentAuthorities.length)
                    trafficAuthority = $trafficAccidentAuthorities.find(
                        (authority) => authority.id == insuranceId,
                    );

                if (trafficAuthority?.id)
                    trafficAuthorityObj.email = trafficAuthority.email;
                break;
            case 20:
                // DVA
                // Data saved in patient details
                break;
            case 25:
            case 26:
                // Defence
                if (isFromQuote) break;

                const dTempArr = insurance.replaceAll(" ", "").split("|");
                insuranceDetails.pidNumber = dTempArr[0];
                insuranceDetails.danNumber = dTempArr[1];
                break;
            case 30:
                // Public in Private
                if (isFromQuote) break;

                const pipTempArr = insurance.replaceAll(" ", "").split("|");
                publicInPrivateObj.email = pipTempArr[0];
                publicInPrivateObj.vendorNumbers = pipTempArr[1];
            default:
                break;
        }
    };
</script>

{#if (isFromCreate || enableChangeEncounterDoctor) && displayMatchPatientStatus.displayStatus}
    <div class="flex items-center gap-x-2 col-span-2">
        {#if matchPatientStatus == 1}
            <Loader />

            <p class={`${displayMatchPatientStatus.textStyle}`}>
                {displayMatchPatientStatus.displayStatus}
            </p>
        {:else if !isObjectEmpty(matchResult) && matchResult.results.length > 0 && !isSamePatient}
            {#if  matchResult.results.length == 1}
                <CustomTooltip tooltipPosition="top">
                    <div class="cursor-pointer" slot="tooltipTrigger">
                        <button
                            class="btn btn-secondary btn-sm"
                            on:click|preventDefault={() => applyPatientDetails(matchResult.results[0].patient)}
                        >
                            Apply matched patient details
                        </button>
                    </div>
                
                    <p>{displayMatchPatientStatus.tooltipSingleMatch}</p>
                </CustomTooltip>
            {:else}
                <CustomTooltip tooltipPosition="top">
                    <div class="cursor-pointer" slot="tooltipTrigger">
                        <button
                            class="btn btn-secondary btn-sm"
                            on:click|preventDefault={viewMatches}
                        >
                            View Matches
                        </button>
                    </div>
                
                    <p>{displayMatchPatientStatus.tooltipMultipleMatches}</p>
                </CustomTooltip>
            {/if}

        {:else}
            <CustomTooltip tooltipPosition="top">
                <div class="cursor-pointer" slot="tooltipTrigger">
                    <p class={`${displayMatchPatientStatus.textStyle}`}>
                        {displayMatchPatientStatus.displayStatus}
                    </p>
                </div>
               
                <p>{displayMatchPatientStatus.tooltip ?? displayMatchPatientStatus.tooltipMatchApplied}</p>
            </CustomTooltip>
           
        {/if}
    </div>
{/if}

<NotificationModal
    bind:isModalOpen={viewMatchesModalOpen}
    modalMaxWidth="max-w-6xl"
    closeModal={() => {
        viewMatchesModalOpen = false; 
    }}
    closeModalFunction={() => {
        viewMatchesModalOpen = false; 
    }}
    title="Match patient based on previous encounters"
    showConfirmBtn={false}    
    cancelText="Close"
>
    <MatchedPatientEncountersListDetails
        bind:matchResult={matchResult}
        onApplyMatch={(encounter) => {
            applyPatientDetails(encounter.patient);
            viewMatchesModalOpen = false;
        }}
    ></MatchedPatientEncountersListDetails>
</NotificationModal>
